import React, {useEffect, useRef, useState, useCallback} from 'react'

const TextEditingOverlay = ({runtime}) => {
    const [nodeId, setNodeId] = useState(() => runtime?.getUiState?.().textEditing?.nodeId ?? null)
    const [draftText, setDraftText] = useState('')
    const [rect, setRect] = useState(null)
    const textareaRef = useRef(null)
    const committingRef = useRef(false)
    // Ref so pointerdown capture handler always reads latest draftText without re-registering on every keystroke
    const draftTextRef = useRef('')
    draftTextRef.current = draftText

    // Subscribe to textEditing session changes from Runtime
    useEffect(() => {
        if (!runtime?.subscribeUiState) return
        const unsubscribe = runtime.subscribeUiState((uiState) => {
            setNodeId(uiState.textEditing?.nodeId ?? null)
        })
        return unsubscribe
    }, [runtime])

    // Initialize local draftText + rect when a new editing session starts
    useEffect(() => {
        if (!nodeId) {
            setRect(null)
            committingRef.current = false
            return
        }
        const node = runtime.getState().nodes.get(nodeId)
        setDraftText(node?.content?.text ?? node?.style?.text ?? '')
        setRect(runtime.measureTextNodeRect?.(nodeId) ?? null)
    }, [nodeId, runtime])

    // Focus + cursor at end when session starts
    useEffect(() => {
        if (!nodeId || !textareaRef.current) return
        textareaRef.current.focus()
        const len = textareaRef.current.value.length
        textareaRef.current.setSelectionRange(len, len)
    }, [nodeId])

    // Auto-resize textarea height
    const autoResize = useCallback(() => {
        const el = textareaRef.current
        if (!el) return
        el.style.height = 'auto'
        el.style.height = el.scrollHeight + 'px'
    }, [])

    useEffect(() => {
        autoResize()
    }, [draftText, autoResize])

    // Re-measure rect when viewport changes (zoom/pan) — Runtime no longer does this
    useEffect(() => {
        if (!nodeId || !runtime?.subscribe) return
        let lastVp = runtime.getState().viewport
        const unsubscribe = runtime.subscribe((state) => {
            const vp = state.viewport
            if (lastVp && vp.scale === lastVp.scale && vp.x === lastVp.x && vp.y === lastVp.y) return
            lastVp = vp
            const newRect = runtime.measureTextNodeRect?.(nodeId)
            if (newRect) setRect(newRect)
        })
        return unsubscribe
    }, [nodeId, runtime])

    // Commit when clicking outside textarea (capture phase fires before Konva handlers)
    useEffect(() => {
        if (!nodeId) return
        function onPointerDown(e) {
            if (textareaRef.current?.contains(e.target)) return
            if (committingRef.current) return
            committingRef.current = true
            runtime.commitTextEditing(nodeId, draftTextRef.current)
            committingRef.current = false
        }
        document.addEventListener('pointerdown', onPointerDown, true)
        return () => document.removeEventListener('pointerdown', onPointerDown, true)
    }, [nodeId, runtime])

    if (!nodeId || !rect) return null

    const node = runtime.getState().nodes.get(nodeId)
    const style = node?.style || {}

    const handleChange = (e) => {
        setDraftText(e.target.value)
    }

    const handleBlur = () => {
        if (committingRef.current) return
        committingRef.current = true
        runtime.commitTextEditing(nodeId, draftText)
        committingRef.current = false
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            e.preventDefault()
            runtime.cancelTextEditing()
        }
    }

    return (
        <textarea
            ref={textareaRef}
            value={draftText}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            style={{
                position: 'absolute',
                left: rect.x,
                top: rect.y,
                width: rect.width,
                minHeight: rect.height,
                fontFamily: style.fontFamily ?? 'Arial',
                fontSize: (style.fontSize ?? 16) * (runtime.getState().viewport?.scale ?? 1),
                color: style.fill ?? '#000',
                textAlign: style.align ?? 'left',
                background: 'transparent',
                border: '1px solid #4a90d9',
                outline: 'none',
                resize: 'none',
                overflow: 'hidden',
                padding: 0,
                margin: 0,
                lineHeight: 'normal',
                boxSizing: 'border-box',
                zIndex: 1000,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
            }}
        />
    )
}

export default TextEditingOverlay
